/**
 * Almacén persistente de tokens en localStorage (patrón Care).
 *
 * - access_token: vida corta (15 min), se inyecta en cada request como Bearer.
 * - refresh_token: vida larga (30d), solo se envía a /api/auth/refresh.
 *
 * Listeners para que múltiples tabs sincronicen estado tras refresh/logout (storage event).
 */

const KEY_ACCESS = 'intrepid.access_token'
const KEY_REFRESH = 'intrepid.refresh_token'
const KEY_USER = 'intrepid.user'

export type StoredUser = {
  id: number
  email: string
  first_name: string
  last_name_paternal: string
  last_name_maternal: string | null
  /** Tipo de cuenta: 'user' (tabla users) o 'admin' (tabla admins, login en /admin/login). */
  audience: 'user' | 'admin'
  tenant_id: number | null
  role: 'owner' | 'technician' | null
  must_change_password: boolean
  company_name: string | null
  signature_path: string | null
  theme: 'light' | 'dark' | 'system'
  menu_pinned: boolean
}

export const tokenStore = {
  getAccessToken(): string | null {
    return localStorage.getItem(KEY_ACCESS)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(KEY_REFRESH)
  },

  getUser(): StoredUser | null {
    const raw = localStorage.getItem(KEY_USER)
    if (!raw) return null
    try {
      return JSON.parse(raw) as StoredUser
    } catch {
      return null
    }
  },

  setSession(access: string, refresh: string, user: StoredUser): void {
    localStorage.setItem(KEY_ACCESS, access)
    localStorage.setItem(KEY_REFRESH, refresh)
    localStorage.setItem(KEY_USER, JSON.stringify(user))
  },

  setTokens(access: string, refresh: string): void {
    localStorage.setItem(KEY_ACCESS, access)
    localStorage.setItem(KEY_REFRESH, refresh)
  },

  setUser(user: StoredUser): void {
    localStorage.setItem(KEY_USER, JSON.stringify(user))
  },

  clear(): void {
    localStorage.removeItem(KEY_ACCESS)
    localStorage.removeItem(KEY_REFRESH)
    localStorage.removeItem(KEY_USER)
  },

  isAuthenticated(): boolean {
    return this.getAccessToken() !== null && this.getRefreshToken() !== null
  },
}
